import React, { useMemo } from 'react';
import Flickity from 'react-flickity-component';
import styled from 'styled-components';
import { BREAKPOINT, MEDIA } from '@constants';
import { PageSlice } from '@contracts';
import Colors from '../Colors';
import LinkButton from '../LinkButton';
import useDesktop from '../useDesktop';
import StorefrontModuleHead from './StorefrontModuleHead';
import TestimonialModuleItem from './TestimonialModuleItem';

interface TestimonialsModuleProps {
  slice: PageSlice;
}

const TestimonialsModule: React.FC<TestimonialsModuleProps> = ({ slice }) => {
  const sliceDetails = slice.primary as any;
  const sliceItems = slice.items as any[];
  const backgroundColor = sliceDetails.background_color ?? Colors.cream;
  const itemColor = sliceDetails.item_color ?? Colors.mint50;
  const isDesktop = useDesktop(BREAKPOINT.laptop);

  const carouselOptions = useMemo(() => {
    return {
      pageDots: false,
      prevNextButtons: false,
      cellAlign: 'left'
    };
  }, []);

  return (
    <Wrapper $backgroundColor={backgroundColor}>
      <StorefrontModuleHead
        overline={sliceDetails.overline}
        heading={sliceDetails.heading}
        subheading={sliceDetails.subheading}
        centeredText={isDesktop}
      />
      {isDesktop ? (
        <TestimonialsRow>
          {sliceItems.map((item, idx) => (
            <TestimonialModuleItem key={idx} itemColor={itemColor} item={item} />
          ))}
        </TestimonialsRow>
      ) : (
        <CarouselWrap>
          <Flickity options={carouselOptions}>
            {sliceItems.map((item, idx) => (
              <TestimonialModuleItem key={idx} itemColor={itemColor} item={item} />
            ))}
          </Flickity>
        </CarouselWrap>
      )}
      {sliceDetails.button_url && (
        <LinkButtonWrapper>
          <LinkButton href={sliceDetails.button_url} isInline>
            {sliceDetails.button_text_key}
          </LinkButton>
        </LinkButtonWrapper>
      )}
    </Wrapper>
  );
};

export default TestimonialsModule;

const Wrapper = styled.div<{ $backgroundColor: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 40px;
  padding: 64px 80px 96px;
  background-color: ${(props) => props.$backgroundColor};
  color: ${Colors.navyBlue};

  & > * {
    max-width: 1400px;
  }

  ${MEDIA.laptop} {
    padding: 56px 0px;
    align-items: flex-start;

    & > * {
      padding-left: 64px;
      padding-right: 64px;
    }
  }

  ${MEDIA.mobileL} {
    padding: 56px 0px;

    & > * {
      padding-left: 24px;
      padding-right: 24px;
    }
  }
`;

const TestimonialsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  column-gap: 32px;

  > * {
    flex: 0 1 calc(33% - 32px);
  }
`;

const CarouselWrap = styled.div`
  width: 100%;
  overflow: visible;
  padding-left: 0px;
  padding-right: 0px;

  .flickity-slider {
    left: 64px !important;
  }

  ${MEDIA.mobileL} {
    .flickity-slider {
      left: 24px !important;
    }
  }
`;

const LinkButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${MEDIA.laptop} {
    align-items: flex-start;
  }
`;
